import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import MaintenanceApprovals from "./MaintenanceApprovals";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import InventoryAssistant from "./InventoryAssistant";
import {
  LayoutDashboard,
  Wrench,
  ClipboardList,
  Database,
  Menu,
  X,
  LogOut,
  User,
  Sun,
  Moon,
  GlassWater,
  Palette,
  Building2,
  ChevronDown,
  FileText,
  CalendarCog,
  Cog,
  Bell,
  FileSpreadsheet,
  Gauge,
  Users,
  TrendingUp,
  Boxes,
  Shield,
  ScrollText,
  HardDrive,
  UserCog,
  PackageOpen,
  Package,
  ShieldCheck,
  IndianRupee,
  AlertTriangle,
  Settings,
  Warehouse,
  Layers,
  Search,
  Mic,
  SlidersHorizontal,
  Plus,
  KeyRound,
  FileJson,
  ShoppingCart,
} from "lucide-react";
import POApprovals from "./POApprovals";

// ROUND S — Sidebar v2: 5 collapsible groups + top-level singletons + bottom
// unchanged. Display grouping only; each item's `module` still gates
// visibility via the same `canSee(item)` predicate below. A group header must
// never render if the user can see none of its children.
const topNavItems = [
  { path: "/dashboard",       label: "Dashboard",       icon: LayoutDashboard, module: "dashboard" },
  { path: "/schedule-setup",  label: "Schedule Setup",  icon: CalendarCog,     module: "schedule_setup" },
  { path: "/purchase",        label: "Purchase",        icon: ShoppingCart,    module: "purchase" },
  { path: "/raw-material",    label: "Raw Material",    icon: Layers,          module: "raw_material" },
];

const pressShopGroup = {
  label: "Press Shop",
  icon: PackageOpen,
  testid: "nav-pressshop-group",
  items: [
    { path: "/production-sheet",   label: "Production Sheet",    icon: ClipboardList, module: "production_sheet" },
    { path: "/tool-history",       label: "Tool Record",         icon: FileText,      module: "tool_history" },
    { path: "/maintenance",        label: "Maintenance",         icon: Wrench,        module: "maintenance" },
    { path: "/spare-parts",        label: "Spare Parts",         icon: Wrench,        module: "maintenance" },
    { path: "/quality/press-shop", label: "Press Shop Red Bin",  icon: PackageOpen,   module: "quality_press_shop" },
  ],
};

const machineNavItems = [
  { path: "/machine-production-sheet", label: "Machine Shop Production", icon: ClipboardList, module: "machine_production_sheet" },
  { path: "/quality/machine-shop", label: "Machine Shop Rejection", icon: Cog, module: "quality_machine_shop" },
];
const machineGroup = { label: "Machine Shop", icon: Cog, testid: "nav-machine-group", items: machineNavItems };

// Assembly — sibling shop to Press/Machine. Same entry screen (MachineProductionSheet
// in mode="assembly"), same data/endpoint/permission; only the join-op rows show here.
const assemblyGroup = {
  label: "Assembly", icon: ClipboardList, testid: "nav-assembly-group",
  items: [
    { path: "/assembly-production-sheet", label: "Assembly Production", icon: ClipboardList, module: "machine_production_sheet" },
  ],
};

const receivingStoreGroup = {
  label: "Receiving Store",
  icon: Warehouse,
  testid: "nav-receiving-group",
  items: [
    { path: "/jobwork",       label: "Job Work",     icon: PackageOpen, module: "jobwork" },
    { path: "/bop-receiving", label: "BOP Receiving", icon: Package,    module: "bop" },
    // ROUND CF — standalone "BOP Purchase Schedule" section retired on owner's
    // request: the same day-wise sheet now downloads from BOP Receiving →
    // Supplier Schedule (option 2). Page + endpoint kept; re-add this line to
    // bring the screen back.
    { path: "/store-godown",  label: "Store Godown", icon: Warehouse,   module: "store" },
  ],
};

// Group name intentionally avoids "Final Inspection" to prevent a visual
// collision with the child of the same name — using "Inspection & FG".
const inspectionFgGroup = {
  label: "Inspection & FG",
  icon: ShieldCheck,
  testid: "nav-inspection-group",
  items: [
    { path: "/rework",                   label: "Rework / Hold",    icon: Wrench,      module: "rework" },
    { path: "/quality/final-inspection", label: "Final Inspection", icon: ShieldCheck, module: "quality_final_inspection" },
    { path: "/planning",                 label: "FG Planning",      icon: PackageOpen, module: "planning" },
  ],
};

const misGroup = {
  label: "MIS",
  icon: TrendingUp,
  testid: "nav-mis-group",
  items: [
    { path: "/inventory",              label: "Inventory",            icon: Boxes,         module: "inventory" },
    { path: "/traceability",           label: "Traceability / Tags",  icon: Boxes,         module: "production_sheet" },
    { path: "/quality/copq",           label: "COPQ",                 icon: IndianRupee,   module: "quality_copq" },
    { path: "/quality/red-tag-cost",   label: "Red Tag Cost",         icon: AlertTriangle, module: "quality_red_tag" },
    { path: "/predictive-analytics",   label: "Analytics",            icon: TrendingUp,    module: "analytics" },
    { path: "/capacity-planning",      label: "Capacity Planning",    icon: Boxes,         module: "capacity_planning" },
    { path: "/manpower-requirement",   label: "Manpower Requirement", icon: UserCog,       module: "capacity_planning" },
    { path: "/previous-data",          label: "Checking Previous Data", icon: FileSpreadsheet, module: "reports_production" },
  ],
};

const bottomNavItems = [
  { path: "/alerts",  label: "Alerts",  icon: Bell,            module: "alerts" },
  { path: "/reports", label: "Reports", icon: FileSpreadsheet },
];

const adminNavItems = [
  { path: "/user-management", label: "User Management", icon: Users, module: "user_management" },
  { path: "/api-keys", label: "API Keys", icon: KeyRound, module: "user_management" },
  { path: "/hr-kpi-export", label: "HR KPI Export", icon: FileJson, module: "user_management" },
  { path: "/rbac-settings", label: "RBAC Settings", icon: Shield, module: "rbac_settings" },
  { path: "/masters", label: "Masters", icon: Database, module: "masters" },
  // Machine Groups moved inside Masters (its tab embeds the same page;
  // the /machine-groups route still works for old links).
  { path: "/tool-life-master", label: "Tool Life Master", icon: Gauge, module: "tool_life_master" },
  { path: "/audit-log", label: "Audit Log", icon: ScrollText, module: "audit_logs" },
  { path: "/backup", label: "Backup & Restore", icon: HardDrive, module: "backup" },
];

// ROUND S — reusable collapsible group. Auto-opens when the current route
// lives inside its children; hides its header entirely when no children pass
// canSee (i.e. items.length === 0). Mirrors the pre-Round-S Machine Shop
// group's visual style so all 5 groups look uniform.
function NavGroup({ group, sidebarOpen, location }) {
  const { label, icon: Icon, testid, items } = group;
  const active = items.some((i) => location.pathname === i.path || location.pathname.startsWith(i.path));
  const [open, setOpen] = useState(false);
  useEffect(() => { if (active) setOpen(true); }, [active]);

  if (items.length === 0) return null;

  return (
    <div className="pt-2">
      {sidebarOpen ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            data-testid={testid}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium flex-1 text-left">{label}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
          {open && (
            <div className="mt-1 space-y-1 ml-4 pl-3 border-l border-border">
              {items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 ${
                      isActive
                        ? "nav-active"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`
                  }
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </>
      ) : (
        // Icon-only sidebar: render children directly (no group toggle to click)
        items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center justify-center px-3 py-2.5 rounded-md transition-all duration-200 ${
                isActive
                  ? "nav-active"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`
            }
            data-testid={`nav-${item.path.slice(1)}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
          </NavLink>
        ))
      )}
    </div>
  );
}

/* Global nav search — filters the user's visible navigation and jumps to a page.
   Ctrl+K (or Cmd+K) focuses it. Pure navigation sugar: no data, no API calls. */
function NavSearch({ items, onGo }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  const hits = q.trim()
    ? items.filter((i) => i.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8)
    : [];
  const go = (item) => { setQ(""); setOpen(false); onGo(item.path); };
  return (
    <div className="relative hidden md:block w-full max-w-sm" data-testid="nav-search">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <input
        ref={ref}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, hits.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && hits[hi]) go(hits[hi]);
          else if (e.key === "Escape") { setQ(""); setOpen(false); ref.current?.blur(); }
        }}
        placeholder="Global Search"
        className="w-full h-9 pl-9 pr-16 rounded-full text-sm bg-muted/60 border border-border/60 outline-none focus:border-primary/40 focus:bg-background focus:ring-2 focus:ring-primary/10 transition-colors placeholder:text-muted-foreground/70"
        data-testid="nav-search-input"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-muted-foreground/60 pointer-events-none" aria-hidden="true">
        <Mic className="w-3.5 h-3.5" />
        <SlidersHorizontal className="w-3.5 h-3.5" />
      </span>
      {open && hits.length > 0 && (
        <div className="absolute top-11 left-0 right-0 rounded-xl border border-border/60 bg-popover shadow-lg overflow-hidden z-50">
          {hits.map((i, idx) => (
            <button
              key={i.path}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); go(i); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                idx === hi ? "bg-primary/10 text-primary" : "hover:bg-accent"
              }`}
            >
              <i.icon className="w-4 h-4 text-muted-foreground" />
              {i.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  // ROUND CL — below lg the sidebar is a FIXED z-50 panel laid over the page.
  // It used to default to open, so on a phone or tablet it covered the left of
  // every screen and stole taps meant for the page (tapping "Log Breakdown" on
  // Maintenance actually hit the Tool History nav link underneath it). Desktop
  // behaviour is unchanged: open by default, collapses to the w-16 rail.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => (typeof window === "undefined" ? true : window.innerWidth >= 1024));
  const [alertCount, setAlertCount] = useState(0);
  const { user, logout, selectedUnit, hasPermission } = useAuth();
  const { theme, toggleTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Tapping a nav link on a phone should close the drawer, not leave it sitting
  // over the page you just opened. Desktop (lg+) is untouched — there the
  // sidebar is part of the layout, not an overlay.
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) setSidebarOpen(false);
  }, [location.pathname]);

  // Escape closes the mobile drawer (same intent as tapping the backdrop). On
  // lg+ the sidebar is part of the layout, not an overlay, so Escape leaves it.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof window !== "undefined" && window.innerWidth < 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  const canSee = (item) => {
    if (!item.module) return true;
    if (item.module === "backup") return user?.role === "management";
    // admin/management see every module. New modules added to the matrix aren't in
    // their stored role doc yet, so gate by role here rather than by hasPermission.
    if (user?.role === "admin" || user?.role === "management") return true;
    // Reports is visible to reports-view users AND to bop-view users (who see only the BOP category inside).
    if (item.path === "/reports") return ["reports_production", "reports_quality", "reports_jobwork", "reports_fg", "reports_machines", "bop"].some((m) => hasPermission(m, "view"));
    return hasPermission(item.module, "view");
  };

  const filteredTopItems = topNavItems.filter(canSee);
  const filteredBottomItems = bottomNavItems.filter(canSee);
  const filteredAdminItems = adminNavItems.filter(canSee);

  // ROUND S — 5 collapsible groups (Press Shop / Machine Shop / Receiving
  // Store / Inspection & FG / MIS). Each group tracks its own open state and
  // auto-expands when the current route lives inside it.
  const groups = [
    { ...pressShopGroup,      items: pressShopGroup.items.filter(canSee) },
    { ...machineGroup,        items: machineGroup.items.filter(canSee) },
    { ...assemblyGroup,       items: assemblyGroup.items.filter(canSee) },
    { ...receivingStoreGroup, items: receivingStoreGroup.items.filter(canSee) },
    { ...inspectionFgGroup,   items: inspectionFgGroup.items.filter(canSee) },
    { ...misGroup,            items: misGroup.items.filter(canSee) },
  ];

  const adminActive = filteredAdminItems.some((i) => location.pathname.startsWith(i.path));
  const [adminOpen, setAdminOpen] = useState(false);
  useEffect(() => {
    if (adminActive) setAdminOpen(true);
  }, [adminActive]);

  useEffect(() => {
    if (selectedUnit) {
      api.get(`/alerts/count?unit_id=${selectedUnit.id}`)
        .then((res) => setAlertCount(res.data.count))
        .catch(() => {});
    }
  }, [selectedUnit]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    // H2: aampl-shell class marks the token-driven shell; tests can key off this.
    <div className="min-h-screen bg-background flex aampl-shell" data-aampl-tokens="v1">
      {/* Tap-anywhere backdrop for the mobile drawer (never shown from lg up). */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`aampl-side fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border/60 transition-all duration-300 ${
          sidebarOpen ? "w-60" : "w-16"
        } ${sidebarOpen ? "" : "max-lg:-translate-x-full"} lg:relative lg:translate-x-0`}
      >
        {/* Logo — AAMPL lockup with unit name beneath */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border/60">
          {sidebarOpen ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/aampl-logo.png" alt="AAMPL"
                   className="aampl-logo-mark w-9 h-9 object-contain flex-shrink-0" />
              <div className="flex flex-col leading-tight min-w-0">
                <span className="font-semibold text-base tracking-tight">AAMPL</span>
                {selectedUnit && (
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">
                    {selectedUnit.name}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <img src="/aampl-logo.png" alt="AAMPL"
                 className="aampl-logo-mark w-8 h-8 object-contain mx-auto" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Unit Selector */}
        {sidebarOpen && selectedUnit && (
          <div className="p-3 border-b border-border">
            <Button
              variant="outline"
              className="w-full justify-between text-sm"
              onClick={() => navigate("/select-unit")}
              data-testid="unit-selector-btn"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span className="truncate">{selectedUnit.name}</span>
              </div>
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {/* ROUND S — Top-level items above the first group */}
            {filteredTopItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                    isActive
                      ? "nav-active before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-blue-500"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  } ${!sidebarOpen && "justify-center"}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm">{item.label}</span>}
              </NavLink>
            ))}

            {/* ROUND S — 5 collapsible groups. Each auto-opens when its
                current route matches; header suppressed if no visible child. */}
            {groups.map((g) => (
              <NavGroup
                key={g.testid}
                group={g}
                sidebarOpen={sidebarOpen}
                location={location}
              />
            ))}

            {/* Alerts + Reports — always at the bottom of the nav proper, above the
                collapsible Administration group. Kept unlabelled per spec. */}
            {filteredBottomItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                    isActive
                      ? "nav-active before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-blue-500"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  } ${!sidebarOpen && "justify-center"}`
                }
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm">{item.label}</span>}
              </NavLink>
            ))}

            {filteredAdminItems.length > 0 && (
              <div className="pt-2">
                {sidebarOpen ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setAdminOpen((o) => !o)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                      data-testid="nav-admin-group"
                    >
                      <Settings className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium flex-1 text-left">Administration</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${adminOpen ? "" : "-rotate-90"}`} />
                    </button>
                    {adminOpen && (
                      <div className="mt-1 space-y-1 ml-4 pl-3 border-l border-border">
                        {filteredAdminItems.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 ${
                                isActive
                                  ? "nav-active"
                                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                              }`
                            }
                            data-testid={`nav-${item.path.slice(1)}`}
                          >
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-medium">{item.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  filteredAdminItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center justify-center px-3 py-2.5 rounded-md transition-all duration-200 ${
                          isActive
                            ? "nav-active"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`
                      }
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                    </NavLink>
                  ))
                )}
              </div>
            )}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className={sidebarOpen ? "w-full justify-start gap-3" : ""}
            data-testid="theme-toggle"
          >
            {theme === "light" ? (
              <Moon className="w-5 h-5" />
            ) : (
              <Sun className="w-5 h-5" />
            )}
            {sidebarOpen && <span className="text-sm">Toggle Theme</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="aampl-top h-14 border-b border-border/60 flex items-center justify-between px-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
            {/* H2 — topbar page title derived from the active nav item so the
                current section is clearly labeled without a per-page header. */}
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground/80">
                {(() => {
                  const combined = [
                    ...topNavItems,
                    ...pressShopGroup.items, ...machineNavItems,
                    ...receivingStoreGroup.items, ...inspectionFgGroup.items, ...misGroup.items,
                    ...bottomNavItems, ...adminNavItems,
                  ];
                  const cur = combined.find(i => location.pathname === i.path || (i.path !== "/" && location.pathname.startsWith(i.path)));
                  return cur ? cur.label : "Overview";
                })()}
              </span>
              <span className="text-sm font-semibold" data-testid="topbar-unit-label">
                {selectedUnit?.name || "Unit"}
              </span>
            </div>
          </div>

          {/* Global search — visible pages only, Ctrl+K to focus */}
          <div className="flex-1 flex justify-center px-2 sm:px-6">
            <NavSearch
              items={[
                ...filteredTopItems,
                ...groups.flatMap((g) => g.items),
                ...filteredBottomItems,
                ...filteredAdminItems,
              ]}
              onGo={(path) => navigate(path)}
            />
          </div>

          {/* Maintenance approvals inbox — plant_head / production_manager only */}
          <MaintenanceApprovals />

          {/* Purchase order approvals inbox — purchase approvers only */}
          <POApprovals />

          {/* Alert Bell */}
          <Button
            variant="ghost"
            size="icon"
            className="relative mr-2"
            onClick={() => navigate("/alerts")}
            data-testid="alert-bell"
          >
            <Bell className="w-5 h-5" />
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="alert-count-badge">
                {alertCount > 99 ? "99+" : alertCount}
              </span>
            )}
          </Button>

          {/* Dashboard effect chooser — Glass / Current (light) / Dark.
              Clicking opens the menu; picking any option applies it to the
              whole dashboard (handled by ThemeContext → root class). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="mr-1 rounded-full bg-muted/60"
                title="Dashboard effect — Glass / Current / Dark"
                data-testid="topbar-theme-toggle"
              >
                {theme === "glass" ? <GlassWater className="w-4 h-4" />
                  : theme === "dark" ? <Moon className="w-4 h-4" />
                  : <Palette className="w-4 h-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setTheme("glass")} data-testid="effect-glass">
                <GlassWater className="w-4 h-4 mr-2 text-sky-500" />
                <span className="flex-1">Glass effect</span>
                {theme === "glass" && <span className="text-sky-600 font-bold">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("light")} data-testid="effect-light">
                <Sun className="w-4 h-4 mr-2 text-amber-500" />
                <span className="flex-1">Current (Light)</span>
                {theme === "light" && <span className="text-sky-600 font-bold">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")} data-testid="effect-dark">
                <Moon className="w-4 h-4 mr-2 text-indigo-400" />
                <span className="flex-1">Dark effect</span>
                {theme === "dark" && <span className="text-sky-600 font-bold">✓</span>}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <span className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium">{user?.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground capitalize">
                    {user?.role?.replace(/_/g, " ")}
                  </span>
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize mt-1">
                  Role: {user?.role?.replace("_", " ")}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Add New — quick-create shortcuts to EXISTING screens (nav only) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="ml-2 h-8 rounded-lg bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white px-3 text-xs font-semibold"
                data-testid="add-new-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate("/masters")}>
                <Database className="w-4 h-4 mr-2" /> Part / Operation (Masters)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/production-sheet")}>
                <ClipboardList className="w-4 h-4 mr-2" /> Production entry
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/schedule-setup")}>
                <CalendarCog className="w-4 h-4 mr-2" /> Monthly schedule
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
          <div key={location.pathname} className="page-transition">
            <Outlet />
          </div>
        </main>

        {/* Inventory Assistant — global floating guide, on every section */}
        <InventoryAssistant unitId={selectedUnit?.id} />
      </div>
    </div>
  );
}
